import React, { FunctionComponent } from 'react';
import { Dialog, DialogBackdrop, DialogProps } from 'reakit/Dialog';
import classNames from 'classnames';
import { Transition, AnimatePresence, motion } from 'framer-motion';
import { CKDialogSize } from '../../common/types';

import Close from '@clearkit/icons/glyphs/Close';

export interface CKDrawerProps extends DialogProps {
  transition?: Transition;
  size?: CKDialogSize;
}

export interface CKDrawerComposition {
  Header: React.FC<CKDrawerSubComponentProps>;
  Heading: React.FC<CKDrawerSubComponentProps>;
  Body: React.FC<CKDrawerSubComponentProps>;
  Footer: React.FC<CKDrawerSubComponentProps>;
}

export const CKDrawer: FunctionComponent<CKDrawerProps> &
  CKDrawerComposition = ({
  transition = { ease: 'circOut', duration: 0.2 },
  children,
  size = CKDialogSize.lg,
  ...rest
}) => {
  const handleClose = () => {
    if (rest && rest.hide) {
      rest.hide();
    }
  };
  const drawerClass = classNames(`ck-drawer`, `ck-drawer--${size}`);
  return (
    <React.Fragment>
      <DialogBackdrop {...rest}>
        {props => (
          <AnimatePresence>
            {props.hidden ? null : (
              <motion.div
                key="backdrop"
                className="ck-dialog-backdrop"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              ></motion.div>
            )}
          </AnimatePresence>
        )}
      </DialogBackdrop>
      <Dialog {...rest}>
        {props => (
          <AnimatePresence>
            {props.hidden ? null : (
              <motion.div
                key="dialog"
                className="ck-dialog-container"
                transition={transition}
                initial={{ x: '100%' }}
                animate={{ x: '0%' }}
                exit={{ x: '100%' }}
              >
                <div {...props} className={drawerClass}>
                  <CKDrawerButton onClick={handleClose} />
                  {children}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        )}
      </Dialog>
    </React.Fragment>
  );
};

interface CKDrawerButtonProps {
  onClick: () => void;
}

export interface CKDrawerSubComponentProps {
  className?: string;
}

const CKDrawerButton: React.FC<CKDrawerButtonProps> = ({ onClick }) => (
  <button className="ck-drawer--close" onClick={onClick}>
    <Close width={24} height={24} />
    <span></span>
  </button>
);

CKDrawer.Header = ({ children, className = '' }) => (
  <div className={`ck-drawer-header ${className}`}>{children}</div>
);

CKDrawer.Heading = ({ children, className = '' }) => (
  <h1 className={`ck-drawer--heading ${className}`}>{children}</h1>
);

CKDrawer.Footer = ({ children, className = '' }) => (
  <div className={`ck-drawer-footer ${className}`}>{children}</div>
);

CKDrawer.Body = ({ children, className = '' }) => (
  <div className={`ck-drawer-body ${className}`}>{children}</div>
);
